import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, uploadUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Folder, RefreshCw, Search, Link2, X } from "lucide-react";

/**
 * PhotoBrowserDialog
 *
 * Windows-Explorer-ish thumbnail grid of every photo in object storage.
 * Also fronts the /admin/photos/claim endpoint so the admin can link
 * `{phone}_image1.jpg` / `{phone}_image2.jpg` files (and other conventions)
 * to members in one click.
 *
 * Files whose phone doesn't match any member are left untouched — they stay
 * in storage as a "future pool" for members who join later.
 */
export default function PhotoBrowserDialog({ open, onOpenChange }) {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [claiming, setClaiming] = useState(false);
    const [query, setQuery] = useState("");
    const [claimReport, setClaimReport] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/photos/browse");
            setFiles(data.files || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load photos");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { if (open) load(); }, [open]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return files;
        return files.filter((f) => f.filename.toLowerCase().includes(q));
    }, [files, query]);

    const totalBytes = filtered.reduce((n, f) => n + (f.size || 0), 0);
    const fmtSize = (n) => (n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / (1024 * 1024)).toFixed(1)} MB`);

    const runClaim = async () => {
        if (!window.confirm(
            "Match every eligible file to its member and rename it to Name_Phone.ext?\n\n" +
            "• {phone}_image1.jpg  → member photo\n" +
            "• {phone}_image2.jpg  → spouse photo\n" +
            "• Files with no matching phone are left in storage untouched."
        )) return;
        setClaiming(true);
        setClaimReport(null);
        try {
            const { data } = await api.post("/admin/photos/claim");
            setClaimReport(data);
            toast.success(`Linked ${data.linked} photo${data.linked === 1 ? "" : "s"}`, {
                description: `${data.already_canonical || 0} already canonical · ${data.unmatched_count || 0} kept in storage (no matching member yet)`,
                duration: 8000,
            });
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Claim failed");
        } finally {
            setClaiming(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-5xl max-h-[90vh] flex flex-col" data-testid="photo-browser-dialog">
                <DialogHeader>
                    <DialogTitle className="rotary-heading text-2xl flex items-center gap-2">
                        <Folder className="h-5 w-5 text-rotary-blue" />
                        Photo library
                    </DialogTitle>
                </DialogHeader>

                {/* Toolbar */}
                <div className="flex flex-wrap items-center gap-2">
                    <div className="relative flex-1 min-w-[240px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search by filename or phone…"
                            className="pl-9"
                            data-testid="photo-browser-search"
                        />
                    </div>
                    <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="photo-browser-refresh">
                        <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </Button>
                    <Button size="sm" onClick={runClaim} disabled={claiming || loading} data-testid="photo-browser-claim">
                        <Link2 className="mr-1.5 h-4 w-4" />
                        {claiming ? "Linking…" : "Link photos to members"}
                    </Button>
                </div>

                {/* Summary line */}
                <p className="text-xs text-muted-foreground">
                    {loading ? "Loading…" : `${filtered.length.toLocaleString()} of ${files.length.toLocaleString()} file${files.length === 1 ? "" : "s"} · ${fmtSize(totalBytes)}`}
                </p>

                {/* Claim report banner */}
                {claimReport && (
                    <div className="rounded-lg border bg-emerald-50 border-emerald-200 px-3 py-2 text-xs text-emerald-800 flex items-center gap-2" data-testid="photo-browser-claim-report">
                        <span className="font-semibold">
                            Linked {claimReport.linked} · Already canonical {claimReport.already_canonical || 0} · Unmatched (kept in storage) {claimReport.unmatched_count || 0}
                        </span>
                        <button
                            type="button"
                            className="ml-auto opacity-60 hover:opacity-100"
                            onClick={() => setClaimReport(null)}
                            aria-label="Dismiss"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    </div>
                )}

                {/* Thumbnail grid */}
                <div className="flex-1 overflow-y-auto rounded-lg border bg-muted/20 p-3">
                    {loading ? (
                        <p className="text-sm text-muted-foreground p-6 text-center">Loading photos…</p>
                    ) : filtered.length === 0 ? (
                        <p className="text-sm text-muted-foreground p-6 text-center">
                            {files.length === 0 ? "No photos in storage yet." : "No photos match your filter."}
                        </p>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="photo-browser-grid">
                            {filtered.map((f) => (
                                <a
                                    key={f.filename}
                                    href={uploadUrl(f.filename)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group rounded-lg border bg-white overflow-hidden hover:shadow-md transition-shadow"
                                    data-testid={`photo-browser-item-${f.filename}`}
                                    title={f.filename}
                                >
                                    <div className="aspect-square bg-slate-100 grid place-items-center overflow-hidden">
                                        <img
                                            src={uploadUrl(f.filename)}
                                            alt={f.filename}
                                            loading="lazy"
                                            className="h-full w-full object-cover group-hover:scale-105 transition-transform"
                                            onError={(e) => { e.target.style.display = "none"; }}
                                        />
                                    </div>
                                    <div className="px-2 py-1.5 text-[11px]">
                                        <p className="truncate font-medium text-rotary-ink" title={f.filename}>{f.filename}</p>
                                        <p className="text-muted-foreground tabular-nums">{fmtSize(f.size || 0)}</p>
                                    </div>
                                </a>
                            ))}
                        </div>
                    )}
                </div>

                <DialogFooter className="gap-2">
                    <Button type="button" onClick={() => onOpenChange(false)}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
